import { t } from '@citrite/translate';
import { ModalAlertProps, warningConfirmModal } from '@citrite/web-ui-component';
// eslint-disable-next-line @citrite/workspace-ui/no-platform-dependency-access-under-src
import { workspaceConfiguration } from '@citrite/workspace-ui-platform';
import { trackAnalyticsEvent } from 'analytics';
import { WorkspaceWebExtensionPromptValues } from 'App/Tile/useBrowserExtensionPrompt';
import { getFromLocalStorage } from 'javascript/sf/Storage';
import { browserExtensionInstallPromptAnalyticsReporter } from 'Workspace/TelemetryEvents/browserExtensionInstallPrompt/createBrowserExtensionInstallPromptAnalyticsReporter';
import { UserDetails } from 'Workspace/UserContext';
import { lastUserDetailsKey } from 'Workspace/UserContext/constants';
import { getStoreIdentifiers } from './browserExtensionHelperMethods';
import { checkBrowserExtensionInstallation } from './browserExtensionPollHelper';

const DONT_SHOW_AGAIN_KEY_PREFIX = 'dontShowAgainBrowserExtensionInstallPrompt';

// allow-unused-export : exported for unit tests
// Scope the "don't show again" preference per (storeGuid, userId) so different
// users on the same browser, and the same user across different stores, all
// have independent preferences.
export const getDontShowAgainKey = () => {
	const userId = getFromLocalStorage<UserDetails>(lastUserDetailsKey).userId;
	const storeGuid = getStoreIdentifiers(workspaceConfiguration.get()).storeGuid;
	return `${DONT_SHOW_AGAIN_KEY_PREFIX}-${storeGuid}-${userId}`;
};

export const showInstallExtensionModal = (
	resumeResourceLaunch: Function,
	workspaceWebExtensionPrompt: string
) => {
	const modalProps: ModalAlertProps = getPropsForModal(
		resumeResourceLaunch,
		workspaceWebExtensionPrompt
	);
	return warningConfirmModal(modalProps);
};

// allow-unused-export : export needed for unit tests
export const getPropsForModal = (
	resumeResourceLaunch: Function,
	workspaceWebExtensionPrompt: string
): ModalAlertProps => {
	const { startCheck, endCheck } = checkBrowserExtensionInstallation();
	let extensionInstallPromiseResolver: Promise<boolean>;
	const isOptional =
		workspaceWebExtensionPrompt === WorkspaceWebExtensionPromptValues.optional;
	const modalProps: ModalAlertProps = {
		title: isOptional
			? t('Workspace:browserExtensionInstallPrompt.recommendedTitle')
			: t('Workspace:browserExtensionInstallPrompt.requiredTitle'),
		confirmButtonText: t('Workspace:browserExtensionInstallPrompt.installButton'),
		cancelButtonText: isOptional
			? t('Workspace:browserExtensionInstallPrompt.notNowButton')
			: t('Workspace:cancel'),
		message: isOptional
			? t('Workspace:browserExtensionInstallPrompt.recommendedMessage')
			: t('Workspace:browserExtensionInstallPrompt.requiredMessage'),
		beforeCloseSuccess: () => {
			extensionInstallPromiseResolver = startCheck();
			return extensionInstallPromiseResolver;
		},
		onCloseSuccess: () => {
			trackInstallEvent();
			extensionInstallPromiseResolver.then(result => {
				endCheck();
				if (result) {
					resumeResourceLaunch();
				}
			});
		}, // also called when do not show again is checked
		onDismiss: endCheck,
		enableSecondaryButtonWhileLoading: true,
	};
	if (isOptional) {
		// do not show again checkbox is only shown for Optional
		modalProps.onDismiss = () => {
			endCheck();
			trackCancelEvent();
			resumeResourceLaunch();
		}; // continues to launch the resource is the user clicks cancel in case of Optional
		modalProps.dontShowAgainKey = getDontShowAgainKey(); // user-specific key so each user has their own "don't show again" preference
	}
	return modalProps;
};

export const shouldNotShowPromptAgain = (workspaceWebExtensionPrompt?: string) => {
	return (
		workspaceWebExtensionPrompt === WorkspaceWebExtensionPromptValues.optional &&
		!!getFromLocalStorage(getDontShowAgainKey())
	);
};

const trackInstallEvent = () => {
	trackAnalyticsEvent(
		browserExtensionInstallPromptAnalyticsReporter.getBrowserExtensionInstallPromptInstallEvent(
			shouldNotShowPromptAgain()
		)
	);
};

const trackCancelEvent = () => {
	trackAnalyticsEvent(
		browserExtensionInstallPromptAnalyticsReporter.getBrowserExtensionInstallPromptCancelledEvent(
			shouldNotShowPromptAgain()
		)
	);
};
